import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./bookingSettings.css";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

export default function BookingSettingsPage() {
  const [choices, setChoices] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedTone, setSelectedTone] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [duration, setDuration] = useState(null);
  const [customDuration, setCustomDuration] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);

  const navigate = useNavigate();

  const isButtonDisabled =
    !selectedTone ||
    !responseTime ||
    !duration ||
    (duration === "Other" && !customDuration);

  useEffect(() => {
    const userStatus = localStorage.getItem("user_status") || "individual";
    const bookingCode = userStatus === "salon" ? "232" : "220";
    const currentCode = localStorage.getItem("onboarding_code");

    if (currentCode !== "complete") {
      localStorage.setItem("onboarding_code", bookingCode);
    }

    sessionStorage.setItem("onboarding_force_route", "/booking-settings");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("booking_settings_closed", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const wasClosed = localStorage.getItem("booking_settings_closed");
    if (wasClosed === "true") {
      localStorage.removeItem("booking_settings_closed");
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const getValidToken = async () => {
      let savedToken =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!savedToken) return null;

      const verifyHeaders = buildHeaders(null);

      const verify = await fetch(`${API_URL}/auth/token/verify/`, {
        method: "POST",
        headers: verifyHeaders,
        body: JSON.stringify({ token: savedToken }),
      });

      if (verify.ok) {
        setToken(savedToken);
        return savedToken;
      }

      const refresh =
        localStorage.getItem("refreshToken") ||
        sessionStorage.getItem("refreshToken");

      if (!refresh) return null;

      const res = await fetch(`${API_URL}/auth/token/refresh/`, {
        method: "POST",
        headers: verifyHeaders,
        body: JSON.stringify({ refresh }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.access) {
        const useLocalStorage =
          !!localStorage.getItem("accessToken") ||
          !!localStorage.getItem("refreshToken");

        if (useLocalStorage) {
          localStorage.setItem("accessToken", data.access);
        } else {
          sessionStorage.setItem("accessToken", data.access);
        }

        setToken(data.access);
        return data.access;
      }

      return null;
    };

    getValidToken();
  }, []);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchChoices(), fetchBookingSettings()]);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChoices = async () => {
    try {
      const headers = buildHeaders(token);

      const res = await fetch(`${API_URL}/service/booking-choices/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error(String(res.status));

      const data = await res.json().catch(() => null);
      setChoices(data);
    } catch (err) {
      console.error("Failed to load booking choices:", err);
    }
  };

  const fetchBookingSettings = async () => {
    try {
      const headers = buildHeaders(token);

      const res = await fetch(`${API_URL}/service/booking-time/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) return;

      const data = await res.json().catch(() => null);
      setSettings(data);

      setSelectedTone(data?.tone_of_voice?.id || null);
      setResponseTime(data?.response_time || null);

      if (
        data?.duration === 0 ||
        data?.duration === "0" ||
        data?.duration === null
      ) {
        setDuration("0");
      } else if (data?.duration) {
        setDuration(String(data.duration));
      }
    } catch (err) {
      console.error("Failed to load booking settings:", err);
    }
  };

  const saveBookingSettings = async () => {
    if (!selectedTone) {
      toast.error("Please select a tone of voice.");
      return;
    }

    if (!responseTime) {
      toast.error("Please select response time.");
      return;
    }

    if (!duration) {
      toast.error("Please select buffer duration.");
      return;
    }

    let finalDuration;

    if (duration === "0") {
      finalDuration = 0;
    } else if (duration === "Other") {
      finalDuration = Number(customDuration);
    } else {
      finalDuration = Number(duration);
    }

    if (Number.isNaN(finalDuration)) {
      toast.error("Please enter a valid duration.");
      return;
    }

    const userTimeZone = getUserTimeZone();

    const payload = {
      duration: finalDuration,
      time_unit: "minutes",
      response_time: responseTime,
      tone_of_voice_id: Number(selectedTone),
      timeZone: userTimeZone,
    };

    try {
      setIsLoading(true);

      const method = settings ? "PUT" : "POST";
      const headers = buildHeaders(token);

      const res = await fetch(`${API_URL}/service/booking-time/`, {
        method,
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to save booking settings");
      }

      localStorage.removeItem("booking_settings_closed");

      try {
        const completeRes = await fetch(`${API_URL}/auth/complete-onboarding/`, {
          method: "POST",
          headers,
        });

        if (!completeRes.ok) {
          console.warn("Failed to complete onboarding on backend, continuing...");
        }
      } catch (completeErr) {
        console.warn("complete-onboarding request failed, continuing...", completeErr);
      }

      localStorage.setItem("user_timezone", userTimeZone);

      const bookingSettings = {
        duration: finalDuration,
        responseTime,
        selectedTone,
        userTimeZone,
      };

      localStorage.setItem("booking_settings", JSON.stringify(bookingSettings));
      localStorage.setItem("onboarding_completed", "true");
      localStorage.setItem("onboarding_code", "complete");

      const userStatus = localStorage.getItem("user_status") || "individual";
      sessionStorage.setItem("onboarding_force_route", "/finish-registration");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      toast.success("Onboarding completed successfully!");
      navigate("/finish-registration", { replace: true });
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Error saving settings.");
    } finally {
      setIsLoading(false);
    }
  };

  const durationOptions = [
    { label: "None", value: "0" },
    { label: "15 minutes", value: "15" },
    { label: "30 minutes", value: "30" },
    { label: "1 hour", value: "60" },
    { label: "2 hours", value: "120" },
    { label: "Other", value: "Other" },
  ];

  const formatDuration = (value) => {
    if (!value) return "Select duration";
    if (value === "0") return "None";

    const minutes = parseInt(value, 10);
    if (!minutes) return "None";

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours && !mins) return `${hours} hour${hours > 1 ? "s" : ""}`;
    if (hours && mins) return `${hours} hour${hours > 1 ? "s" : ""} ${mins} min`;

    return `${mins} min`;
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && !isButtonDisabled && !isLoading) {
        e.preventDefault();
        saveBookingSettings();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [isButtonDisabled, isLoading, selectedTone, responseTime, duration, customDuration]);

  if (!token) {
    return <div className="no-token"></div>;
  }

  if (isLoading || !choices) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="booking-wrapper">
      <motion.div
        className="booking-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="header">
          <div className="titles">
            <h1>Booking settings</h1>
            <p>Configure how customers can book your services</p>
          </div>
        </div>

        <div className="section-row">
          <div className="left">
            <h2>Set buffer time between appointments to clean or take a break</h2>
            <label className="label">Choose duration</label>

            <div className="booking-select-wrapper">
              <div
                className="booking-select-display"
                onClick={() => setOpenDropdown(!openDropdown)}
              >
                <span>
                  {!duration
                    ? "Select duration"
                    : duration === "0"
                    ? "None"
                    : duration === "Other"
                    ? customDuration
                      ? formatDuration(customDuration)
                      : "Custom"
                    : formatDuration(duration)}
                </span>

                <span className={`booking-arrow ${openDropdown ? "open" : ""}`}>
                  ▾
                </span>
              </div>

              <AnimatePresence>
                {openDropdown && (
                  <motion.div
                    className="booking-dropdown"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                  >
                    {durationOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`booking-dropdown-option ${
                          duration === opt.value ? "active" : ""
                        }`}
                        onClick={() => {
                          setDuration(opt.value);
                          if (opt.value !== "Other") setCustomDuration("");
                          setOpenDropdown(false);
                        }}
                      >
                        <span>{opt.label}</span>
                        <div
                          className={`booking-option-check ${
                            duration === opt.value ? "checked" : ""
                          }`}
                        ></div>
                      </div>
                    ))}

                    {duration === "Other" && (
                      <motion.div
                        className="dropdown-custom-input"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          min="1"
                          placeholder="Enter duration in minutes..."
                          value={customDuration}
                          onChange={(e) => setCustomDuration(e.target.value)}
                          className="dropdown-custom-field"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && customDuration) {
                              e.preventDefault();
                              const minutes = parseInt(customDuration, 10);
                              if (!Number.isNaN(minutes) && minutes > 0) {
                                setDuration(minutes.toString());
                                setCustomDuration(minutes.toString());
                                setOpenDropdown(false);
                              }
                            }
                          }}
                        />
                        <button
                          className="dropdown-apply-btn"
                          onClick={() => {
                            const minutes = parseInt(customDuration, 10);
                            if (Number.isNaN(minutes) || minutes <= 0) {
                              toast.error("Please enter a valid duration");
                              return;
                            }

                            setDuration(minutes.toString());
                            setCustomDuration(minutes.toString());
                            setOpenDropdown(false);
                          }}
                        >
                          Apply
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="right">
            <h2>When Halper will answer to your clients</h2>
            <div className="radio-list">
              {choices.response_time_choices?.map((item, i) => (
                <label key={i} className="radio-option">
                  <span>{item.value || item}</span>
                  <input
                    type="radio"
                    value={item.value || item}
                    checked={responseTime === (item.value || item)}
                    onChange={() => setResponseTime(item.value || item)}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="tone-section">
          <h2>Tone of voice</h2>
          <div className="tone-grid">
            {choices.tone_of_voice_options?.map((tone) => (
              <motion.label
                key={tone.id}
                className={`tone-card ${selectedTone === tone.id ? "active" : ""}`}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedTone(tone.id)}
              >
                <div className="tone-content">
                  <div>
                    <h3>{tone.title}</h3>
                    <p>{tone.description}</p>
                  </div>
                  <span className="tone-check"></span>
                </div>
              </motion.label>
            ))}
          </div>
        </div>

        <div className="footer">
          <button
            onClick={saveBookingSettings}
            className="btn-primary"
            disabled={isButtonDisabled || isLoading}
            style={{
              opacity: isButtonDisabled ? 0.5 : 1,
              cursor: isButtonDisabled ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? "Saving..." : "Finish registration"}
          </button>
          <p className="step">Step 5/5</p>
        </div>
      </motion.div>
    </div>
  );
}